/**
 * resolveClientNextStep — cerveau du cockpit client.
 * ──────────────────────────────────────────────────
 * Miroir de resolveNextAction() côté agence, mais écrit avec des mots
 * rassurants pour le client et sans exposer la mécanique interne.
 *
 * Retourne :
 *   {
 *     tone       : "accent" | "success" | "neutral",
 *     eyebrow    : string,      // ex. "PROCHAINE ÉTAPE"
 *     title      : string,      // langage client
 *     description: string | null,
 *     icon       : LucideIcon | null,
 *     primaryCta : { label, onClick } | null,  // rare — client passif
 *     secondaryCta: { label, onClick } | null,
 *   }
 *
 * Aucune couleur "danger" côté client — on ne stresse jamais le client.
 * Un blocage devient « Votre agence travaille pour vous ».
 */
import {
  Sparkles, CheckCircle2, Wallet, Truck, PartyPopper, ClipboardCheck,
  Clock, MessageCircle,
} from "lucide-react";
import { STEP_STATUS } from "@/data/mockData";

const fmtMoney = (v, currency = "EUR") => {
  if (v == null || v === "") return "";
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v} ${currency}`;
  }
};

const CLIENT_STEP_COPY = {
  reservation:  "Votre réservation est en cours d'enregistrement.",
  acompte:      "Votre acompte est attendu pour poursuivre.",
  admin:        "Nous constituons votre dossier administratif.",
  paiement:     "Le règlement est en cours de vérification.",
  check:        "Contrôle qualité effectué en atelier.",
  ct:           "Passage au contrôle technique.",
  esthetique:   "Préparation esthétique de votre véhicule.",
  mecanique:    "Remise en état mécanique en cours.",
  pret:         "Validation finale avant remise.",
  livraison:    "Rendez-vous de livraison à programmer.",
  cpi:          "Envoi imminent de votre certificat provisoire (CPI).",
};

const clientCopyFor = (step) =>
  step?.client_message?.trim() ||
  CLIENT_STEP_COPY[step?.key] ||
  "Prochaine étape de votre parcours.";

export function resolveClientNextStep({ vehicle, payment, delivery, actions = {} }) {
  const { openMessages, openSlotsPicker } = actions;
  const steps = vehicle.steps || [];
  const pay = payment?.summary || {};
  const sched = delivery || vehicle.deliveryScheduling || null;

  // ── 0. Dossier terminé — célébration ─────────────────────────────────
  if (vehicle.isFinished) {
    return {
      tone: "success",
      eyebrow: "VOTRE VÉHICULE EST À VOUS",
      title: "Merci pour votre confiance.",
      description:
        "Votre dossier est clôturé. Vous pouvez toujours retrouver ici l'historique et vos documents.",
      icon: PartyPopper,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 0bis. Créneaux à choisir (priorité maximale côté client) ─────────
  if (sched?.status === "proposed" && (sched.proposals?.length || 0) > 0) {
    return {
      tone: "accent",
      eyebrow: "VOTRE VÉHICULE EST PRÊT 🎉",
      title: "Choisissez votre créneau de livraison",
      description:
        "Sélectionnez la date qui vous convient — un simple tap suffit.",
      icon: Truck,
      primaryCta: openSlotsPicker
        ? { label: "Voir les créneaux", onClick: openSlotsPicker }
        : null,
      secondaryCta: null,
    };
  }

  // ── 0ter. Créneau confirmé ───────────────────────────────────────────
  if (sched?.status === "confirmed" && vehicle.deliveryDate) {
    return {
      tone: "success",
      eyebrow: "RENDEZ-VOUS CONFIRMÉ",
      title: `Livraison le ${vehicle.deliveryDate}${vehicle.deliveryTime ? " · " + vehicle.deliveryTime : ""}`,
      description: sched.location
        ? `Nous vous accueillons ${sched.location}.`
        : (vehicle.deliveryLocation
            ? `Nous vous accueillons ${vehicle.deliveryLocation}.`
            : "Nous vous accueillons prochainement pour la remise de votre véhicule."),
      icon: CheckCircle2,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 1. Paiement à effectuer par le client ────────────────────────────
  if (pay.status === "pending" && pay.delivery_blocked && pay.amount_due) {
    return {
      tone: "accent",
      eyebrow: "ACTION ATTENDUE",
      title: `Règlement attendu : ${fmtMoney(pay.amount_due, pay.currency)}`,
      description:
        "Nous vous transmettons les modalités par email. N'hésitez pas à joindre votre conseiller pour toute question.",
      icon: Wallet,
      primaryCta: openMessages
        ? { label: "Contacter mon conseiller", onClick: openMessages, icon: MessageCircle }
        : null,
      secondaryCta: null,
    };
  }

  // ── 2. Paiement déclaré, en cours de vérification ────────────────────
  if (pay.status === "declared") {
    return {
      tone: "accent",
      eyebrow: "EN COURS DE VÉRIFICATION",
      title: "Votre paiement est en cours de vérification",
      description:
        "Nous confirmons la réception dans les meilleurs délais.",
      icon: Clock,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 3. Étape en cours ────────────────────────────────────────────────
  const active = steps.find((s) => s.status === STEP_STATUS.IN_PROGRESS);
  if (active) {
    return {
      tone: "accent",
      eyebrow: "EN COURS",
      title: active.label || active.title,
      description: clientCopyFor(active),
      icon: ClipboardCheck,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 4. Étape bloquée — on ne stresse jamais ──────────────────────────
  const blocked = steps.find((s) => s.status === STEP_STATUS.BLOCKED);
  if (blocked) {
    return {
      tone: "accent",
      eyebrow: "NOTRE ÉQUIPE TRAVAILLE POUR VOUS",
      title: "Nous finalisons un point administratif",
      description:
        "Aucune démarche de votre part n'est nécessaire. Nous revenons vers vous très vite.",
      icon: Sparkles,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 5. Étape à venir ─────────────────────────────────────────────────
  const upcoming = steps.find((s) => s.status === STEP_STATUS.UPCOMING);
  if (upcoming) {
    return {
      tone: "accent",
      eyebrow: "PROCHAINE ÉTAPE",
      title: upcoming.label || upcoming.title,
      description: clientCopyFor(upcoming),
      icon: Sparkles,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 6. Toutes étapes ok mais livraison non programmée ────────────────
  if (!vehicle.deliveryDate) {
    return {
      tone: "accent",
      eyebrow: "PROCHAINE ÉTAPE",
      title: "Votre livraison arrive",
      description:
        "Votre véhicule est prêt. Votre conseiller vous propose très bientôt un créneau de livraison.",
      icon: Truck,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 7. Livraison programmée ──────────────────────────────────────────
  return {
    tone: "success",
    eyebrow: "LIVRAISON PROGRAMMÉE",
    title: `Rendez-vous le ${vehicle.deliveryDate}${vehicle.deliveryTime ? " · " + vehicle.deliveryTime : ""}`,
    description: vehicle.deliveryLocation
      ? `Nous vous accueillons ${vehicle.deliveryLocation}.`
      : "Nous vous accueillons prochainement pour la remise de votre véhicule.",
    icon: CheckCircle2,
    primaryCta: null,
    secondaryCta: null,
  };
}

/**
 * Dernière étape validée (rassurance).
 */
export function resolveLastCompleted(vehicle) {
  const steps = vehicle.steps || [];
  // On prend la dernière étape "termine" avant la première non-terminée
  let last = null;
  for (const s of steps) {
    if (s.status === STEP_STATUS.DONE) last = s;
    else break;
  }
  if (!last) return null;
  return {
    key: last.key,
    label: last.label || last.title,
    completed_at: last.updated_at || last.completed_at || null,
  };
}
